import "zod/compile";
import { z } from "zod";

export const SopFeedbackSourceKindSchema = z.enum([
  "comment",
  "task_action",
  "terminal_reason",
  "chat_message",
  "chat_feedback",
  "operator_instruction",
]);
export const SopFeedbackVisibilitySchema = z.enum(["shared", "private"]);
export const SopImprovementStatusSchema = z.enum([
  "pending",
  "accepted",
  "dismissed",
  "stale",
  "superseded",
]);
export const SopAssessmentStatusSchema = z.enum([
  "waiting",
  "pending",
  "running",
  "complete",
  "failed",
]);
export const SopImprovementImpactWarningSchema = z.object({
  code: z.enum([
    "recorded_automation_unchanged",
    "structure_changed",
    "new_unautomated_step",
    "parameters_changed",
    "managed_procedure",
  ]),
  stepIds: z.array(z.string()),
  message: z.string(),
});
export type SopImprovementImpactWarning = z.infer<
  typeof SopImprovementImpactWarningSchema
>;
export const SopFeedbackReviewSchema = z.object({
  proposalIds: z.array(z.uuid()),
  baseVersionId: z.uuid(),
  /** Publication discharged the review; retain its proposal/base provenance for audit. */
  dischargedAt: z.iso.datetime().optional(),
});
export type SopFeedbackReview = z.infer<typeof SopFeedbackReviewSchema>;
export type SopFeedbackSourceKind = z.infer<typeof SopFeedbackSourceKindSchema>;
export type SopFeedbackVisibility = z.infer<typeof SopFeedbackVisibilitySchema>;
export type SopImprovementStatus = z.infer<typeof SopImprovementStatusSchema>;
export type SopAssessmentStatus = z.infer<typeof SopAssessmentStatusSchema>;

export const SopLearningSettingsSchema = z.object({
  taskId: z.uuid(),
  enabled: z.boolean(),
  canManage: z.boolean(),
});
export const SopFindingSchema = z.object({
  kind: z.enum([
    "correction",
    "no_change",
    "needs_context",
    "needs_manual_repair",
  ]),
  summary: z.string().max(4000),
  receiptIds: z.array(z.uuid()),
  sopIds: z.array(z.uuid()),
  scope: z.enum([
    "standing",
    "conditional",
    "one_task",
    "discussion",
    "unclear",
  ]),
});
export type SopFinding = z.infer<typeof SopFindingSchema>;

export const SopTaskScopeSchema = z.object({
  taskId: z.uuid(),
  parentTaskId: z.uuid().nullable(),
  lifecycleRevision: z.number().int().nonnegative(),
  status: z.string(),
  feedbackRevision: z.number().int().nonnegative(),
  // Older persisted manifests did not include the per-task setting.
  learningEnabled: z.boolean().optional(),
});
export const SopUsageEvidenceSchema = z.object({
  taskId: z.uuid(),
  sopId: z.uuid(),
  sopVersionId: z.uuid().nullable(),
  sopVersion: z.number().int().positive().optional(),
  kind: z.enum(["read", "replayed", "embedded"]),
  sourceId: z.string(),
  status: z.string(),
});
export type SopUsageEvidence = z.infer<typeof SopUsageEvidenceSchema>;

/** No source content here: safe to expose in the shared review queue. */
export const SopEvidenceReferenceSchema = z.object({
  id: z.uuid(),
  taskId: z.uuid(),
  sourceKind: SopFeedbackSourceKindSchema,
  sourceId: z.string(),
  authorUserId: z.string(),
  visibility: SopFeedbackVisibilitySchema,
  revision: z.number().int(),
  deleted: z.boolean(),
  createdAt: z.string(),
  canReadContent: z.boolean(),
});
